import posthog from 'posthog-js';

import type { RootState } from '../redux/store';
import { store } from '../redux/store';
import { ANALYTICS_APP, ANALYTICS_EVENTS, FEEDBACK_SURVEY, PROJECT_GROUP_TYPE } from './constants';
import { markFlagsReloading } from './flagsStatus';
import analyticsHelpers from './helpers';
import type {
  AnalyticsUserProperties,
  ClientCardChatOpenedProperties,
  ClientDetailsOpenedProperties,
  ClientDetailsTabViewedProperties,
  ClientMoreInfoOpenedProperties,
  ClientStageManuallyChangedProperties,
  FunnelViewChangedProperties,
  FunnelViewExitedProperties,
  FunnelTableLegacyViewRequestedProperties,
  UserLoggedInProperties,
  UserLoggedOutProperties,
  BaseEventProperties,
  NotificationCenterToggledProperties,
  NotificationCenterActionUsedProperties,
  CallsEventProperties,
  ClientsFilteredProperties,
  ClientsSearchedProperties,
  SessionExpiredProperties,
  ProjectGroupProperties,
} from './types';

function getUserContext(): BaseEventProperties {
  const state = store.getState() as RootState;
  const { user } = state;
  return {
    projectId: user?.projectId,
    permissions: user?.permissions ?? undefined,
    isAdmin: user?.isAdmin,
    countryCode: user?.countryCode,
  };
}

function captureEvent(eventName: string, properties: Record<string, unknown>): void {
  const baseProps = getUserContext();
  const allProps = { ...baseProps, ...properties };
  const props = analyticsHelpers.convertKeysToSnakeCase(allProps) as Record<string, unknown>;
  posthog.capture(eventName, props);
}

/** Sends an event the user explicitly asked for, even before they accept performance cookies. */
function sendConsentedEvent(eventName: string, properties: Record<string, unknown>): Promise<Response> | null {
  const apiKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
  const apiHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;
  const { user } = store.getState() as RootState;
  if (!apiKey || !apiHost || !user?.id) return null;

  const props = analyticsHelpers.convertKeysToSnakeCase({
    ...getUserContext(),
    email: user.email,
    ...properties,
  }) as Record<string, unknown>;

  return fetch(new URL('/i/v0/e/', apiHost), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    keepalive: true,
    body: JSON.stringify({
      api_key: apiKey,
      event: eventName,
      distinct_id: String(user.id),
      properties: {
        ...props,
        app: ANALYTICS_APP,
        $current_url: window.location.href,
        $host: window.location.host,
        $pathname: window.location.pathname,
      },
    }),
  });
}

function captureConsentedEvent(eventName: string, properties: Record<string, unknown>): void {
  sendConsentedEvent(eventName, properties)?.catch(() => undefined);
}

/**
 * Sends one answer to the "Feedback abierto" survey; throws when PostHog does not accept it.
 * Writing and submitting feedback is the user's consent to send it, so it skips the cookie gate.
 */
async function submitFeedback(feedback: string, canContact: boolean): Promise<void> {
  const { user } = store.getState() as RootState;
  const contactAnswer = canContact ? 'Sí' : 'No';
  const request = sendConsentedEvent('survey sent', {
    $survey_id: FEEDBACK_SURVEY.id,
    $survey_name: FEEDBACK_SURVEY.name,
    $survey_completed: true,
    $survey_submission_id: crypto.randomUUID(),
    [`$survey_response_${FEEDBACK_SURVEY.questionId}`]: feedback,
    [`$survey_response_${FEEDBACK_SURVEY.contactQuestionId}`]: contactAnswer,
    $survey_questions: [
      { id: FEEDBACK_SURVEY.questionId, question: FEEDBACK_SURVEY.question, response: feedback },
      { id: FEEDBACK_SURVEY.contactQuestionId, question: FEEDBACK_SURVEY.contactQuestion, response: contactAnswer },
    ],
    ...(user?.projectId ? { $groups: { [PROJECT_GROUP_TYPE]: String(user.projectId) } } : {}),
  });
  if (!request) throw new Error('Analytics is not configured');
  const response = await request;
  if (!response.ok) throw new Error(`PostHog rejected the feedback with status ${response.status}`);
}

let lastIdentity: { userId: string | number; properties?: AnalyticsUserProperties } | undefined;
let lastProjectGroup: { projectId: number; properties: ProjectGroupProperties } | undefined;

/** Before init it only keeps the identity: `posthog.group` would leave the flags request stuck for the session. */
function identify(userId: string | number, properties?: AnalyticsUserProperties): void {
  lastIdentity = { userId, properties };
  if (!posthog.__loaded) return;
  const distinctId = String(userId);
  if (posthog.get_distinct_id() !== distinctId) markFlagsReloading();
  posthog.identify(distinctId, properties);
  // Puts the user and every later event in their project's group, so usage can be read per project.
  if (properties?.projectId) posthog.group(PROJECT_GROUP_TYPE, String(properties.projectId));
}

let lastProjectGroupKey: string | null = null;

/** Skips repeats: AuthRoute remounts on every navigation, and each call with properties sends a $groupidentify. */
function setProjectGroupProperties(projectId: number, properties: ProjectGroupProperties): void {
  lastProjectGroup = { projectId, properties };
  if (!posthog.__loaded) return;
  const projectGroupKey = JSON.stringify([projectId, properties]);
  if (projectGroupKey === lastProjectGroupKey) return;
  lastProjectGroupKey = projectGroupKey;
  const groupProperties = analyticsHelpers.convertKeysToSnakeCase({ ...properties }) as Record<string, unknown>;
  posthog.group(PROJECT_GROUP_TYPE, String(projectId), groupProperties);
}

const hasIdentity = (): boolean => lastIdentity !== undefined;

/** Call after a late init (consent given while signed in), with the first flags load disabled while identified. */
function replayIdentity(): void {
  if (!lastIdentity) return;
  identify(lastIdentity.userId, lastIdentity.properties);
  if (lastProjectGroup) setProjectGroupProperties(lastProjectGroup.projectId, lastProjectGroup.properties);
  markFlagsReloading();
  posthog.reloadFeatureFlags();
}

/** `posthog.reset` also drops the consent opt-in, so it is restored for the next user of the same tab. */
function reset(): void {
  lastProjectGroupKey = null;
  lastIdentity = undefined;
  lastProjectGroup = undefined;
  if (!posthog.__loaded) return;
  const wasCapturing = posthog.is_capturing();
  posthog.reset();
  if (wasCapturing) posthog.opt_in_capturing({ captureEventName: false });
  markFlagsReloading();
  posthog.reloadFeatureFlags();
}

function captureClientCardChatOpened(properties: ClientCardChatOpenedProperties): void {
  captureEvent(ANALYTICS_EVENTS.CLIENT_CARD_CHAT_OPENED, { ...properties });
}

function captureClientDetailsOpened(properties: ClientDetailsOpenedProperties): void {
  captureEvent(ANALYTICS_EVENTS.CLIENT_DETAILS_OPENED, { ...properties });
}

function captureClientDetailsTabViewed(properties: ClientDetailsTabViewedProperties): void {
  captureEvent(ANALYTICS_EVENTS.CLIENT_DETAILS_TAB_VIEWED, { ...properties });
}

function captureClientMoreInfoOpened(properties: ClientMoreInfoOpenedProperties): void {
  captureEvent(ANALYTICS_EVENTS.CLIENT_MORE_INFO_OPENED, { ...properties });
}

function captureFunnelViewChanged(properties: FunnelViewChangedProperties): void {
  captureEvent(ANALYTICS_EVENTS.FUNNEL_VIEW_CHANGED, { ...properties });
}

function captureFunnelViewExited(properties: FunnelViewExitedProperties): void {
  captureEvent(ANALYTICS_EVENTS.FUNNEL_VIEW_EXITED, { ...properties });
}

function captureFunnelTableLegacyViewRequested(properties: FunnelTableLegacyViewRequestedProperties): void {
  captureConsentedEvent(ANALYTICS_EVENTS.FUNNEL_TABLE_LEGACY_VIEW_REQUESTED, { ...properties });
}

function captureClientStageManuallyChanged(properties: ClientStageManuallyChangedProperties): void {
  captureEvent(ANALYTICS_EVENTS.CLIENT_STAGE_MANUALLY_CHANGED, { ...properties });
}

function captureUserLoggedIn(properties: UserLoggedInProperties): void {
  captureEvent(ANALYTICS_EVENTS.USER_LOGGED_IN, { ...properties });
}

function captureUserLoggedOut(properties: UserLoggedOutProperties): void {
  captureEvent(ANALYTICS_EVENTS.USER_LOGGED_OUT, { ...properties });
}

function captureNotificationCenterToggled(properties: NotificationCenterToggledProperties): void {
  captureEvent(ANALYTICS_EVENTS.NOTIFICATION_CENTER_TOGGLED, { ...properties });
}

function captureNotificationCenterActionUsed(properties: NotificationCenterActionUsedProperties): void {
  captureEvent(ANALYTICS_EVENTS.NOTIFICATION_CENTER_ACTION_USED, { ...properties });
}

function captureCallsEvent(properties: CallsEventProperties): void {
  const { action, ...rest } = properties;

  const CALL_EVENT_NAME_BY_ACTION: Record<CallsEventProperties['action'], string> = {
    permission_requested: ANALYTICS_EVENTS.CALL_PERMISSION_REQUESTED,
    initiated: ANALYTICS_EVENTS.CALL_INITIATED,
    terminated: ANALYTICS_EVENTS.CALL_TERMINATED,
  };

  captureEvent(CALL_EVENT_NAME_BY_ACTION[action], rest);
}

function captureClientsFiltered(properties: ClientsFilteredProperties): void {
  captureEvent(ANALYTICS_EVENTS.CLIENTS_FILTERED, { ...properties });
}

function captureClientsSearched(properties: ClientsSearchedProperties): void {
  captureEvent(ANALYTICS_EVENTS.CLIENTS_SEARCHED, { ...properties });
}

function captureSessionExpired(properties: SessionExpiredProperties): void {
  captureEvent(ANALYTICS_EVENTS.SESSION_EXPIRED, { ...properties });
}

const analytics = {
  identify,
  setProjectGroupProperties,
  hasIdentity,
  replayIdentity,
  reset,
  captureEvent,
  captureClientCardChatOpened,
  captureClientDetailsOpened,
  captureClientDetailsTabViewed,
  captureClientMoreInfoOpened,
  captureFunnelViewChanged,
  captureFunnelViewExited,
  captureFunnelTableLegacyViewRequested,
  captureClientStageManuallyChanged,
  captureUserLoggedIn,
  captureUserLoggedOut,
  captureNotificationCenterToggled,
  captureNotificationCenterActionUsed,
  captureCallsEvent,
  captureClientsFiltered,
  captureClientsSearched,
  captureSessionExpired,
  submitFeedback,
};

export default analytics;
